/**
 * CompanyPagesListing — Directory of all company pages
 * Route: /app/company-pages
 *
 * Shows all company pages in the lending network.
 * User can search and click to view any company.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { InputText }   from 'primereact/inputtext';
import { Button }      from 'primereact/button';
import { Avatar }      from 'primereact/avatar';
import { Skeleton }    from 'primereact/skeleton';
import { Toast }       from 'primereact/toast';
import { Paginator }   from 'primereact/paginator';
import { Message }     from 'primereact/message';
import { BreadCrumb }  from 'primereact/breadcrumb';
import { companyPageService } from '../../services/companyPageService';

const initials = (name) =>
    name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

const CompanyPagesListing = () => {
    const navigate = useNavigate();
    const toast    = useRef(null);

    const [pages,       setPages]       = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [search,      setSearch]      = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [total,       setTotal]       = useState(0);
    const [page,        setPage]        = useState(1);
    const [error,       setError]       = useState(null); // { message, status }
    const perPage = 12;

    // ── Fetch company pages from DB ────────────────────────────────────────────
    const fetchPages = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await companyPageService.getPages({ search, perPage, page });
            if (res.success) {
                setPages(res.data?.data || []);
                setTotal(res.data?.total || 0);
            } else {
                // Bug fix: show specific error message, not generic one
                const errMsg = res.message || 'Failed to load company pages';
                setError({ message: errMsg, status: res.status || null });
            }
        } catch (err) {
            // Bug fix: surface HTTP status and error detail clearly
            const status  = err?.response?.status;
            const detail  = err?.response?.data?.message;
            let message = 'Could not connect to server. Please check your network.';
            if (status === 401) message = 'Session expired. Please log in again.';
            else if (status === 403) message = 'You do not have permission to view company pages.';
            else if (status === 404) message = 'Company pages endpoint not found (404). Contact support.';
            else if (status === 500) message = `Server error (500): ${detail || 'Internal server error. Contact support.'}`;
            else if (detail) message = detail;
            setError({ message, status });
        } finally {
            setLoading(false);
        }
    }, [search, page]);

    useEffect(() => { fetchPages(); }, [fetchPages]);

    // ── Search ────────────────────────────────────────────────────────────────
    const handleSearch = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            setSearch(searchInput.trim());
            setPage(1);
        }
    };

    // ── Skeleton cards ────────────────────────────────────────────────────────
    const renderSkeletons = () =>
        Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="col-12 md:col-6 lg:col-4">
                <div className="p-3 border-round-xl h-full" style={{ border: '1px solid var(--surface-border)', background: 'var(--surface-card)' }}>
                    <div className="flex align-items-center gap-3 mb-3">
                        <Skeleton shape="square" size="56px" borderRadius="12px" />
                        <div className="flex-1">
                            <Skeleton height="1rem" width="60%" className="mb-2" />
                            <Skeleton height="0.75rem" width="80%" />
                        </div>
                    </div>
                    <Skeleton height="0.75rem" className="mb-2" />
                    <Skeleton height="0.75rem" width="70%" className="mb-3" />
                    <Skeleton height="2rem" borderRadius="8px" />
                </div>
            </div>
        ));

    // ── Company card ──────────────────────────────────────────────────────────
    const CompanyCard = ({ company }) => (
        <div className="col-12 md:col-6 lg:col-4">
            <div
                className="p-3 border-round-xl h-full flex flex-column"
                style={{
                    border: '1px solid var(--surface-border)',
                    background: 'var(--surface-card)',
                    transition: 'box-shadow 0.15s, background 0.15s',
                    cursor: 'pointer',
                }}
                onClick={() => navigate(`/app/company-pages/${company.slug}`)}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.background = 'var(--surface-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.background = 'var(--surface-card)'; }}
            >
                {/* Header: logo + name */}
                <div className="flex align-items-start gap-3 mb-3">
                    {company.logo_url ? (
                        <img
                            src={company.logo_url}
                            alt={company.name}
                            style={{ width: '56px', height: '56px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0, border: '1px solid var(--surface-border)' }}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    ) : (
                        <Avatar
                            label={initials(company.name)}
                            shape="square"
                            style={{ width: '56px', height: '56px', borderRadius: '12px', flexShrink: 0, background: 'var(--primary-100)', color: 'var(--primary-color)', fontWeight: '700', fontSize: '1.1rem' }}
                        />
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-900 mb-1 line-clamp-1" style={{ fontSize: '0.95rem' }}>{company.name}</div>
                        {company.tagline && (
                            <div className="text-xs text-color-secondary line-clamp-2" style={{ lineHeight: '1.4' }}>{company.tagline}</div>
                        )}
                    </div>
                </div>

                {/* Industry badge */}
                {company.industry && (
                    <div className="flex flex-wrap gap-1 mb-2">
                        <span className="px-2 py-1 border-round text-xs" style={{ background: 'var(--surface-ground)', color: 'var(--text-color-secondary)', border: '1px solid var(--surface-border)' }}>
                            <i className="pi pi-tag mr-1" style={{ fontSize: '0.65rem' }} />
                            {company.industry}
                        </span>
                    </div>
                )}

                {/* Location */}
                {(company.hq_city || company.hq_state) && (
                    <div className="flex align-items-center gap-1 text-xs text-color-secondary mb-2">
                        <i className="pi pi-map-marker" style={{ fontSize: '0.7rem' }} />
                        <span>{[company.hq_city, company.hq_state].filter(Boolean).join(', ')}</span>
                    </div>
                )}

                {/* Stats */}
                <div className="flex gap-3 text-xs text-color-secondary mb-3 mt-auto pt-2" style={{ borderTop: '1px solid var(--surface-border)' }}>
                    <div className="flex align-items-center gap-1">
                        <i className="pi pi-users" style={{ fontSize: '0.7rem' }} />
                        <span>{company.follower_count || 0} followers</span>
                    </div>
                    <div className="flex align-items-center gap-1">
                        <i className="pi pi-briefcase" style={{ fontSize: '0.7rem' }} />
                        <span>{company.agent_count || 0} employees</span>
                    </div>
                </div>

                {/* View button — replaces Follow */}
                <Button
                    icon="pi pi-eye"
                    label="View Page"
                    className="w-full p-button-sm p-button-outlined"
                    style={{ borderRadius: '8px', fontSize: '0.8rem' }}
                    onClick={(e) => { e.stopPropagation(); navigate(`/app/company-pages/${company.slug}`); }}
                />
            </div>
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    const breadcrumbHome = { icon: 'pi pi-home', command: () => navigate('/app/leadflow/feed') };
    const breadcrumbItems = [
        { label: 'Lending Network', command: () => navigate('/app/leadflow/feed') },
        { label: 'For Business' },
    ];

    return (
        <div className="grid">
            <div className="col-12">
                <Toast ref={toast} />

                {/* Breadcrumb navigation */}
                <BreadCrumb model={breadcrumbItems} home={breadcrumbHome} className="mb-3 border-none p-0 surface-ground border-round" />

                {/* Header */}
                <div className="flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                    <div>
                        <h1 className="m-0 text-900">Company Pages</h1>
                        <span className="text-color-secondary text-sm">Discover companies on the Lending Network</span>
                    </div>

                    {/* Search */}
                    <div className="flex gap-2">
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                                onKeyDown={handleSearch}
                                placeholder="Search companies…"
                                style={{ width: '260px' }}
                            />
                        </span>
                        <Button
                            icon="pi pi-search"
                            className="p-button-primary"
                            onClick={handleSearch}
                            tooltip="Search"
                            tooltipOptions={{ position: 'top' }}
                        />
                        {search && (
                            <Button
                                icon="pi pi-times"
                                className="p-button-outlined p-button-secondary"
                                onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                                tooltip="Clear search"
                                tooltipOptions={{ position: 'top' }}
                            />
                        )}
                    </div>
                </div>

                {/* Error display — specific, not generic */}
                {error && (
                    <div className="mb-3">
                        <Message
                            severity="error"
                            text={error.status ? `[${error.status}] ${error.message}` : error.message}
                            className="w-full"
                            style={{ justifyContent: 'flex-start' }}
                        />
                        <Button
                            label="Retry"
                            icon="pi pi-refresh"
                            className="p-button-sm p-button-outlined p-button-danger mt-2"
                            onClick={fetchPages}
                        />
                    </div>
                )}

                {/* Total count */}
                {!loading && !error && total > 0 && (
                    <div className="text-sm text-color-secondary mb-3">
                        <i className="pi pi-building mr-1" />
                        {total} {total === 1 ? 'company' : 'companies'} found
                        {search && <span> for "<strong>{search}</strong>"</span>}
                    </div>
                )}

                {/* Grid */}
                <div className="grid">
                    {loading ? renderSkeletons() : !error && pages.length === 0 ? (
                        <div className="col-12">
                            <div className="flex flex-column align-items-center py-8 text-center">
                                <i className="pi pi-building text-6xl mb-3" style={{ color: 'var(--surface-300)' }} />
                                <div className="text-color-secondary">
                                    {search ? `No companies found for "${search}"` : 'No company pages have been created yet.'}
                                </div>
                            </div>
                        </div>
                    ) : (
                        pages.map(company => <CompanyCard key={company.slug} company={company} />)
                    )}
                </div>

                {/* Paginator */}
                {total > perPage && (
                    <Paginator
                        first={(page - 1) * perPage}
                        rows={perPage}
                        totalRecords={total}
                        onPageChange={e => setPage(Math.floor(e.first / perPage) + 1)}
                        className="mt-4"
                    />
                )}
            </div>
        </div>
    );
};

export default CompanyPagesListing;
